import {Button, Group, Modal, Select, Stack, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import {requiresHomeYard} from 'src/assets/workers/home-yard'
import {useDispatchYards} from 'src/dispatch/use-dispatch'

import type {WorkerType} from '../worker-types/schemas'
import {
    type CreateWorkerInput,
    type EmploymentClassification,
    employmentClassificationOptions,
    type Worker,
} from './schemas'
import {validateWorkerName} from './worker-name-validation'

type WorkerFormValues = {
    name: string
    workerTypeId: string | null
    employmentClassification: EmploymentClassification | null
    email: string
    phone: string
    homeDispatchYardId: string | null
}

type WorkerFormModalProps = {
    opened: boolean
    workerTypes: WorkerType[]
    onClose: () => void
    onCreate: (input: CreateWorkerInput) => Promise<Worker>
    onSaved: () => void
}

export function WorkerFormModal({
    opened,
    workerTypes,
    onClose,
    onCreate,
    onSaved,
}: WorkerFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const {data: yards = []} = useDispatchYards()

    const form = useForm<WorkerFormValues>({
        initialValues: {
            name: '',
            workerTypeId: null,
            employmentClassification: null,
            email: '',
            phone: '',
            homeDispatchYardId: null,
        },
        validate: {
            name: (value) => validateWorkerName(value),
            workerTypeId: (value) => (value ? null : 'Worker type is required'),
            employmentClassification: (value) =>
                value ? null : 'Employment classification is required',
            homeDispatchYardId: (value, values) =>
                value || !requiresHomeYard(workerTypes, values.workerTypeId)
                    ? null
                    : 'Yard is required',
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        form.setValues({
            name: '',
            workerTypeId: workerTypes[0]?.id ?? null,
            employmentClassification: null,
            email: '',
            phone: '',
            homeDispatchYardId: null,
        })
        form.clearErrors()
    }, [opened, workerTypes])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: WorkerFormValues) {
        if (!values.workerTypeId || !values.employmentClassification) {
            return
        }

        setIsSubmitting(true)
        try {
            await onCreate({
                name: values.name.trim(),
                workerTypeId: values.workerTypeId,
                employmentClassification: values.employmentClassification,
                email: values.email,
                phone: values.phone,
                ...(requiresHomeYard(workerTypes, values.workerTypeId)
                    ? {homeDispatchYardId: values.homeDispatchYardId}
                    : {}),
            })
            onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    const typeOptions = workerTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }))

    return (
        <Modal opened={opened} onClose={handleClose} title="Add worker" centered>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                    <Select
                        label="Type"
                        withAsterisk
                        data={typeOptions}
                        {...form.getInputProps('workerTypeId')}
                    />
                    <Select
                        label="Employment classification"
                        withAsterisk
                        placeholder="Select classification"
                        data={employmentClassificationOptions}
                        {...form.getInputProps('employmentClassification')}
                    />
                    {requiresHomeYard(workerTypes, form.values.workerTypeId) ? (
                        <Select
                            label="Yard"
                            withAsterisk
                            placeholder="Select yard"
                            data={yards.map((yard) => ({value: yard.id, label: yard.name}))}
                            {...form.getInputProps('homeDispatchYardId')}
                        />
                    ) : null}
                    <TextInput label="Email" {...form.getInputProps('email')} />
                    <TextInput label="Phone" {...form.getInputProps('phone')} />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            Add worker
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
